<template>
  <div
    v-if="islast == false &&
      (gap.extension == 1 ||
        (
          gap.gap >= $store.state.arrivalTable.earliestGap &&
          gap.gap <= $store.state.arrivalTable.latestGap
        )
      )"
    class="gap"
    :class="{
      'is-closed': closed,
      'is-inactive': !active
    }"
  >
    <div
      v-if="active"
      class="gap__cell gap__cell--time"
    >
      {{ time }}

      <c-tooltip v-if="!gapIsInPast">
        <template #popup>
          {{ $t('timeSlotInfo') }}
        </template>
      </c-tooltip>
    </div>

    <div
      v-if="closed"
      class="gap__cell gap__cell--closed"
    >
      {{ $t('closed') }}
    </div>

    <template v-else>
      <div class="gap__cell gap__cell--info">
        <c-tooltip :disabled="!active || gapIsInPast">
          <span class="gap__waiting action action--tertiary">
            <span class="action__inner">{{ gap.wait }} Min</span>
          </span>

          <template #popup>
            {{ $t('estimatedWait') }}<br>
            <c-icon
              type="timer"
              :size="1.25"
            /> <strong>{{ gap.wait }}{{ active ? 'min' : '' }}</strong>
          </template>
        </c-tooltip>
      </div>

      <div class="gap__cell gap__cell--booking">
        <c-action
          v-if="!soldOut && !buttonDisabled"
          color="tertiary"
          @click="checkCart"
        >
          <span v-if="gapIsInPast">{{ $t('soldOut') }}</span>
          <template v-else>
            <span v-if="!reservation && showPrice">{{ humanPrice }}</span>
            <c-icon
              v-if="!reservation && !soldOut"
              type="add"
              :size="0.8125"
            />
            <span v-if="reservation && !soldOut && active">{{ $t('reserve') }}</span>
            <span v-if="reservation && !soldOut && !active">{{ $t('free') }}</span>
            <span v-if="reservation && !soldOut && gap.discount && active">&nbsp;+ %</span>
          </template>
        </c-action>

        <c-tooltip
          v-if="soldOut || buttonDisabled"
          :position="{ xs: 'left', sm: 'center' }"
          :disabled="!active || gapIsInPast"
        >
          <strong class="gap__soldout">{{ $t('soldOut') }}</strong>

          <template #popup>
            {{ $t('ticketsAtBoxOffice.info') }}
            <template v-if="gap.wait">
              <br>{{ $t('estimatedWait') }}<br>
              <c-icon
                type="timer"
                :size="1.25"
              /> <strong>{{ gap.wait }}{{ active ? 'min' : '' }}</strong>
            </template>
          </template>
        </c-tooltip>
      </div>

      <div
        v-if="!gapIsInPast && gap.discount > 0 && !soldOut"
        class="gap__cell gap__cell--discount"
      >
        <c-tooltip
          :position="{ xs: 'left', sm: 'left', xl: 'center' }"
          :disabled="!active"
        >
          <span class="gap__discount-text">{{ gap.discount }}&nbsp;%</span>
          <template #popup>
            {{ $t('discountInfo') }}
          </template>
        </c-tooltip>
      </div>

      <div
        v-if="!gapIsInPast && active && tourSpots"
        class="gap__cell gap__cell--tour"
      >
        <c-action
          color="tertiary"
          text-transform="none"
          @click="addTour"
        >
          {{ $tc('spots', tourSpots, { count: tourSpots }) }}
        </c-action>
      </div>
    </template>
  </div>
</template>

<script>
import jump from 'jump.js'

import CAction from './action.vue'
import CIcon from './icon.vue'
import CTooltip from './tooltip.vue'

import eventBus from '../eventBus.js'
import moveButton from '../functions/moveButton.js'
import priceHelper from '../functions/priceHelper.js'
import timeFromGap from '../functions/timeFromGap.js'

import { GAP_ALL_DAY } from '../settings.js'

export default {
  name: 'CGap',

  components: {
    CAction,
    CIcon,
    CTooltip
  },

  props: {
    id: {
      type: String,
      required: true
    },
    type: {
      type: String,
      required: true
    },
    gap: {
      type: Object,
      required: true
    },
    gapdate: {
      type: String,
      required: true
    },
    isFirst: {
      type: Boolean
    },
    islasttwo: {
      type: Boolean,
      default: false
    },
    islastfour: {
      type: Boolean,
      default: false
    },
    islast: {
      type: Boolean,
      default: false
    },
    reservation: Boolean
  },

  computed: {
    closed () {
      return this.gap.is_open === false
    },
    soldOut () {
      return this.gap.tickets === 0
    },
    showPrice () {
      return !this.soldOut
    },
    time () {
      return timeFromGap(this.gap.gap)
    },
    active () {
      return this.type === 'active'
    },
    highlight () {
      return this.gap.highlight
    },
    gapIsInPast () {
      return this.gap.tickets === -1
    },
    buttonDisabled () {
      if (this.gap.tickets <= 0 || this.soldOut) {
        return true
      }

      return false
    },
    humanPrice () {
      return priceHelper.format(this.calcPrice(this.gap), this.$i18n.locale)
    },
    tourSpots () {
      const startTime = this.time.slice(0, 5)
      const toursSpotsPerGap = this.$store.state.toursSpotsPerGap

      let offsetSpots = toursSpotsPerGap[startTime] ?? 0

      if (this.isFirst) {
        const currentGapOffsetDate = new Date(this.gapdate)
        currentGapOffsetDate.setHours(
          parseInt(this.time.slice(0, 2)) - 1,
          parseInt(this.time.slice(3, 5))
        )

        const nextGapOffsetDate = new Date(this.gapdate)
        nextGapOffsetDate.setHours(currentGapOffsetDate.getHours())
        nextGapOffsetDate.setMinutes(currentGapOffsetDate.getMinutes() + 30)

        const currentGapOffsetTime = currentGapOffsetDate.toTimeString().slice(0, 5)
        const nextGapOffsetTime = nextGapOffsetDate.toTimeString().slice(0, 5)

        offsetSpots += toursSpotsPerGap[currentGapOffsetTime] ?? 0
        offsetSpots += toursSpotsPerGap[nextGapOffsetTime] ?? 0
      }

      return offsetSpots
    }
  },
  methods: {
    calcPrice (gap) {
      const ticketsSelected = this.$store.state.ticketSelection.tickets
      const articles = this.$store.state.articles
      let price = 0

      ticketsSelected.forEach(selection => {
        const prices = articles[selection]

        const ticketPrice = priceHelper.getArticlePrice(
          prices.price,
          prices.price_new,
          prices.price_new_date,
          this.gapdate
        )

        price += ticketPrice * ((gap.discount <= 0) ? 1 : ((100 - gap.discount) / 100))
      })

      return price / 100
    },

    checkCart (event) {
      if (this.islasttwo || this.islastfour) {
        const self = this
        eventBus.emit('showMessage', {
          title: this.$t('lastInfo.title'),
          message: this.$t(this.islastfour ? 'lastInfo.message.four' : 'lastInfo.message.two', [this.$store.state.gaps[this.gapdate].openingHours]),
          buttons: [
            {
              label: this.$t('lastInfo.buttons.continue'),
              color: 'primary',
              callback: function () {
                self.checkIfCartIsFilled(event)
              }
            },
            {
              label: this.$t('lastInfo.buttons.discard'),
              color: 'secondary'
            }
          ]
        })
      } else {
        this.checkIfCartIsFilled(event)
      }
    },

    checkIfCartIsFilled (event) {
      if (
        this.$store.state.cart.tickets.total_articles === 0 &&
        this.$store.state.cart.events.total_articles > 0 &&
        this.$store.state.cart.events.date === this.$store.state.ticketSelection.selectedDate
      ) {
        const self = this
        eventBus.emit('showMessage', {
          title: this.$t('eventInfo.title'),
          message: this.$t('eventInfo.message'),
          buttons: [
            {
              label: this.$t('eventInfo.buttons.yes'),
              color: 'primary',
              callback: function () { self.addToCart(event) }
            },
            {
              label: this.$t('eventInfo.buttons.no'),
              color: 'secondary'
            }
          ]
        })
      } else {
        this.addToCart(event)
      }
    },

    addToCart (event) {
      // EET
      this.$tracking.eetGapSelected()

      if (this.reservation) {
        this.$store.dispatch('addReservationToCart', {
          gap: this.gap,
          amount: this.$store.state.selectedReservationsAmount
        })
      } else {
        this.$store.dispatch('addTicketsToCart', { gap: this.gap })
        jump('#special-events', { duration: 500 })
      }

      this.$store.commit('showContainerSidebar', true)
      moveButton(event, document.querySelector('#cart-overview'))
    },

    async addTour () {
      const gap = {
        gap: GAP_ALL_DAY,
        date: this.gap.date
      }

      const selectedTime = {
        start: new Date(this.gapdate),
        end: new Date(this.gapdate)
      }

      selectedTime.start.setHours(this.time.slice(0, 2), this.time.slice(3, 5))
      selectedTime.end.setHours(this.time.slice(8, 10), this.time.slice(11, 13))

      if (!this.isFirst) {
        selectedTime.start.setHours(selectedTime.start.getHours() + 1)
      }

      selectedTime.end.setHours(selectedTime.end.getHours() + 1)

      this.$store.commit('setToursSelectedTime', selectedTime)

      // Wait for the tour adding request to finish…
      await this.$store.dispatch('addTicketsToCart', { gap })

      // …then redirect to the tours booking page. This ensures the cart has
      // been successfully updated on the server before the next view
      // fetches the cart.
      this.$router.push({ name: `booking-tours-${this.$i18n.locale}` })
    }
  }
}
</script>

<i18n locale="en">
{
  "reserve": "Reserve",
  "free": "Free",
  "closed": "Closed",
  "soldOut": "sold out",
  "discountInfo": "Discount for this time-slot for online booking only.",
  "discount": "Discount",
  "spots": "0 spots | 1 spot | {count} spots",
  "estimatedWait": "Your estimated wating time:",
  "ticketsAtBoxOffice": {
    "title": "Tickets only at cash register",
    "info": "Tickets are only available at the cash register on site."
  },
  "eventInfo": {
    "title": "Attention",
    "message": "Please note that the selected special event ticket already contains a regular ticket. Do you still want to add these tickets to your cart?",
    "buttons": {
      "yes": "Yes",
      "no": "No"
    }
  },
  "lastInfo": {
    "title": "Please note",
    "message": {
      "two": "This time slot only allows a brief visit. Our opening times today are from {0} h.",
      "four": "Today's opening hours are from {0} h."
    },
    "buttons": {
      "continue": "Continue",
      "discard": "Discard"
    }
  },
  "timeSlotInfo": "This is only your time slot for arrival at Miniatur Wunderland. You may stay as long as you like during opening hours."
}
</i18n>

<i18n locale="de">
{
  "reserve": "Reservieren",
  "free": "Frei",
  "closed": "Geschlossen",
  "soldOut": "ausgebucht",
  "discountInfo": "Der Ticketpreis für diese Ankunftszeit ist bei Onlinebuchung reduziert.",
  "discount": "Rabatt",
  "spots": "0 Plätze | 1 Platz | {count} Plätze",
  "estimatedWait": "Ihre Voraussichtliche Wartezeit:",
  "ticketsAtBoxOffice": {
    "title": "Tickets nur an der Kasse",
    "info": "Tickets sind nur an der Kasse vor Ort verfügbar."
  },
  "eventInfo": {
    "title": "Hinweis",
    "message": "Bitte beachten Sie, dass Sonderveranstaltungen bereits Eintrittskarten beinhalten. Möchten Sie diese Tickets trotzdem dem Warenkorb hinzufügen?",
    "buttons": {
      "yes": "Ja",
      "no": "Nein"
    }
  },
  "lastInfo": {
    "title": "Hinweis",
    "message": {
      "two": "Hierbei handelt es sich um ein Zeitfenster für einen Kurzbesuch. Die heutigen Öffnungszeiten sind von {0} Uhr.",
      "four": "Die Öffnungszeiten heute sind von {0} Uhr."
    },
    "buttons": {
      "continue": "Weiter",
      "discard": "Verwerfen"
    }
  },
  "timeSlotInfo": "Hierbei handelt es sich nur um Ihre Einlasszeit ins Miniatur Wunderland. Ihre Aufenthaltsdauer innerhalb der Öffnungszeiten ist unbegrenzt."
}
</i18n>

<style lang="scss">
@import "../css/variables";

$breakpoint-icon-text: $breakpoint-sm;

.gap {
  display: grid;
  grid-template:
    auto min-content /
    $grid-columns-gap-xs;
  grid-template-areas:
    "time info booking  tour"
    ".    .    discount .";
  place-items: center;

  padding: 0.25rem;

  font-size: 0.875rem;
  border-bottom: $border-gap;

  &.is-closed {
    color: $color-strong;
    background-color: $color-light;
  }

  &.has-highlight {
    color: #fff;
    background-color: $color-primary;
    border-bottom: none;
  }

  &.is-inactive {
    grid-template-columns: $grid-columns-gap-inactive;
    grid-template-areas: "info booking discount";
  }

  @media (min-width: $breakpoint-gap-sm) {
    grid-template-columns: $grid-columns-gap-sm;
    padding: 0.5rem;
  }

  @media (min-width: $breakpoint-gap-md) {
    grid-template:
      auto /
      $grid-columns-gap-md;
    grid-template-areas: "time info booking discount tour";

    font-size: 0.9375em;

    &.is-inactive {
      font-size: 0.875rem;
    }
  }

  @media (min-width: $breakpoint-xl) {
    height: 3.125rem;
  }
}

.gap__cell--time {
  grid-area: time;

  @media (min-width: $breakpoint-gap-md) {
    font-size: 1rem;
  }
}

.gap__cell--closed {
  grid-area: booking;

  .is-inactive & {
    grid-column: 1 / -1;
  }
}

.gap__cell--info {
  grid-area: info;
}

.gap__cell--booking {
  grid-area: booking;
}

.gap__cell--discount {
  grid-area: discount;

  @media (min-width: $breakpoint-gap-md) {
    font-size: 1rem;
  }
}

.gap__cell--tour {
  grid-area: tour;
}

.gap__soldout {
  color: $color-primary;
  font-size: 0.9em;
}

.gap__free {
  font-size: 0.9em;
}

.gap__discount-text {
  font-weight: 700;
  color: $color-primary;
}

.gap__waiting {
  opacity: 0.7;
}
</style>
